import React, { useState } from 'react'

import { useTranslation } from '@mc/i18n'
import { TileCaption, TileImage } from '@mc/design-system'

import Image from 'components/LegacyImage'
import { STATIC_IMAGE_BASE } from 'libs/constants'

import { trackCampHeroTrailerClick } from './events'
import CampHeroTrailerModal from './CampHeroTrailerModal'
import { CtaStrategy } from './Ctas'
import { TrailerButton } from './TrailerButton'
import { IncludedBanner } from './IncludedBanner'
import { Description } from './Description'
import { useSessionsLandingPageData } from '../SessionsLandingPageDataProvider'
import style from '../../CampPageScreen.module.scss'

export const CampHero = () => {
  const { camp } = useSessionsLandingPageData()
  const {
    id,
    fullDescription,
    title,
    trailer,
    nameplateTitle,
    instructors,
    images: { primary16x9 },
    isLive,
    slug,
  } = camp
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslation('@mc/next')

  const trailerVideoId = trailer?.brightcoveVideoId
  const trailerVideoUuid = trailer?.mediaUuid

  const onWatchTrailerClick = () => {
    setShowModal(true)
    trackCampHeroTrailerClick(
      slug,
      isLive,
      `${id}_${trailerVideoId ?? trailerVideoUuid}`,
    )
  }

  return (
    <div data-testid='default-hero'>
      <div className='row no-gutters mc-mb-5'>
        <div className={'col-12 col-lg-6'}>
          <div className={`mc-h-100 ${style.heroImageContainer}`}>
            <div
              className={`position-relative mc-w-100 mc-h-100 ${style.heroImage}`}
            >
              <TileCaption position='left top' padding={6}>
                <div className={style.heroWordmark}>
                  <Image
                    src={`${STATIC_IMAGE_BASE}/sessions/sessions_white.svg`}
                    width={225}
                    height={40}
                    alt={t('sessions.campHero.logoAlt')}
                  />
                </div>
              </TileCaption>
              <TileImage
                image={
                  <Image
                    src={primary16x9}
                    alt={instructors[0]?.name}
                    priority
                    layout='fill'
                    // as image bounding box rescales, focus on the horizontal center and vertical top quarter of image
                    objectPosition='50% 25%'
                  />
                }
              />
              {(trailerVideoId || trailerVideoUuid) && (
                <TrailerButton
                  className='mc-m-4'
                  onClick={onWatchTrailerClick}
                />
              )}
            </div>
          </div>
        </div>
        <div className='col-12 col-lg-6 d-flex align-items-center'>
          <div className={style.heroText}>
            <div className='mc-text--center mc-p-7'>
              <div className='row'>
                <div className='col-12'>
                  <h1
                    className={`${style.heroTextOverride} mc-pt-2 mc-text--brand mc-text--uppercase`}
                  >
                    {nameplateTitle}
                  </h1>
                  <div className={`mc-my-2 ${style.heroSeparator}`} />
                  <h4 className='mc-text-h4'>{instructors[0]?.name}</h4>
                </div>
              </div>

              {fullDescription && <Description description={fullDescription} />}

              <div className={`mc-pb-6 ${style.heroCtas}`}>
                <CtaStrategy camp={camp} />
              </div>

              <IncludedBanner />
            </div>
          </div>
        </div>
      </div>
      {(trailerVideoId || trailerVideoUuid) && (
        <CampHeroTrailerModal
          show={showModal}
          onClose={() => setShowModal(false)}
          videoId={trailerVideoId as string}
          mediaUuid={trailerVideoUuid}
          contentId={`${id}_${trailerVideoId ?? trailerVideoUuid}`}
          contentTitle={title}
          slug={slug}
          isLive={isLive}
        />
      )}
    </div>
  )
}
